import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  CssBaseline,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PeopleAltRounded,
  LogoutRounded,
  CorporateFareRounded,
  // CircleRounded,
} from "@mui/icons-material";
import { LOGO_URL } from "../constants/brand";
import Swal from "sweetalert2";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logout } from "../features/auth/authSlice";
import { dispatchRouteData } from "../routes/dataDispatcher";

const drawerWidth = 240;

type NavItem = {
  label: string;
  path: string;
  icon: React.ElementType;
};

const navItems: NavItem[] = [
  { label: "User", path: "/user", icon: PeopleAltRounded },
  {
    label: "Organizations",
    path: "/organizations",
    icon: CorporateFareRounded,
  },
  { label: "Patients", path: "/patients", icon: PeopleAltRounded },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);

  const userInitials = useMemo(() => {
    if (!user?.name) {
      return "JD";
    }
    return user.name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join("");
  }, [user]);

  const handleProfileMenu = (event: React.MouseEvent<HTMLElement>) =>
    setProfileAnchor(event.currentTarget);
  const handleCloseProfileMenu = () => setProfileAnchor(null);

  useEffect(() => {
    dispatchRouteData(location.pathname, dispatch);
  }, [location.pathname, dispatch]);

  const handleNavigateProfile = () => {
    handleCloseProfileMenu();
    if (user?._id) {
      navigate(`/user/${user._id}`);
    } else {
      navigate("/settings");
    }
  };

  const handleLogout = async () => {
    handleCloseProfileMenu();
    const result = await Swal.fire({
      title: "Log out?",
      text: "You will need to log back in to access the workspace.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.error.main,
      cancelButtonColor: theme.palette.primary.main,
      confirmButtonText: "Log out",
    });
    if (!result.isConfirmed) {
      return;
    }
    dispatch(logout());
    await Swal.fire({
      title: "Signed out",
      text: "You have been logged out successfully.",
      icon: "success",
      confirmButtonColor: theme.palette.primary.main,
      timer: 1500,
    });
    navigate("/login");
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: "100%",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar
          sx={{
            minHeight: 80,
            px: 4,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box
            component="img"
            src={LOGO_URL}
            alt="Logo"
            sx={{
              height: 54,
              width: 164,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={user?.name ?? "Profile"}>
              <IconButton
                onClick={handleProfileMenu}
                size="small"
                sx={{ p: 0.5 }}
              >
                <Avatar
                  src={user?.imageUrl}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    fontSize: 14,
                    borderRadius: "50%",
                  }}
                >
                  {userInitials}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={handleCloseProfileMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                elevation: 8,
                sx: { mt: 1.5, minWidth: 180, borderRadius: 1 },
              }}
            >
              <MenuItem
                onClick={handleNavigateProfile}
                sx={{ py: 1, px: 2, fontSize: 13, fontWeight: 600 }}
              >
                Profile
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "error.main",
                }}
              >
                Logout
              </MenuItem>
            </Menu>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                {user?.name || "Guest User"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {user?.email || ""}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar sx={{ minHeight: 80 }} />

        <List sx={{ flexGrow: 1, px: 0, pt: 2 }}>
          {navItems.map((item) => {
            const selected = location.pathname.startsWith(item.path);
            return (
              <ListItemButton
                key={item.label}
                selected={selected}
                onClick={() => navigate(item.path)}
                sx={{
                  mb: 0.5,
                  py: 1.5,
                  borderRadius: 0,
                  margin: 0,
                  borderColor: "primary.main",
                  backgroundColor: "transparent",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: selected ? "primary.main" : "text.secondary",
                    ml: 1,
                  }}
                >
                  <item.icon sx={{ fontSize: selected ? 22 : 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: selected ? 700 : 500,
                    fontSize: 14,
                    color: selected ? "primary.main" : "text.primary",
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ px: 0, pb: 4 }}>
          <Divider sx={{ mx: 0, borderBottomWidth: 1 }} />
          <ListItemButton
            onClick={handleLogout}
            sx={{
              py: 1.5,
              borderRadius: 0,
              margin: 0,
              borderLeft: "4px solid transparent",
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "text.secondary", ml: 1 }}>
              <LogoutRounded sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: 14,
                color: "text.primary",
              }}
            />
          </ListItemButton>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          width: `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Toolbar sx={{ minHeight: 80 }} />
        <Box
          sx={{
            flexGrow: 1,
            px: 3,
            py: 2,
            bgcolor: "background.default",
            overflow: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
